import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getIncidentWorkflowStats, 
  getIncidentWorkflowBoard, 
  assignIncident, 
  escalateIncident,
  bulkUpdateIncidents,
  getAdminList,
  addIncidentNote,
  updateIncident
} from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useAdminMode } from '../context/AdminModeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { toast } from 'sonner';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  LayoutGrid,
  List,
  AlertTriangle,
  Clock,
  Users,
  CheckCircle,
  XCircle,
  ArrowRight,
  MessageSquare,
  UserPlus,
  AlertCircle,
  Loader2,
  RefreshCw,
  Filter,
  ChevronRight,
  Eye,
  MoreVertical,
  ArrowUpRight,
  Zap,
  Target,
  TrendingUp,
  FileText,
  Calendar,
  MapPin,
  Heart,
  Building2,
  Wrench,
  Clipboard,
  Cloud,
  Shield,
  Send,
  GripVertical
} from 'lucide-react';

// Category icons
const CATEGORY_ICONS = {
  abuse_misconduct: AlertTriangle,
  injury_medical: Heart,
  facility_premises: Building2,
  equipment: Wrench,
  operational: Clipboard,
  behavior_conduct: Users,
  environmental: Cloud,
  other_safety: Shield,
};

// Status configuration
const STATUS_CONFIG = {
  new: { label: 'New', color: 'bg-blue-500', textColor: 'text-blue-500', bgLight: 'bg-blue-500/10' },
  in_review: { label: 'In Review', color: 'bg-yellow-500', textColor: 'text-yellow-500', bgLight: 'bg-yellow-500/10' },
  action_required: { label: 'Action Required', color: 'bg-orange-500', textColor: 'text-orange-500', bgLight: 'bg-orange-500/10' },
  escalated: { label: 'Escalated', color: 'bg-red-500', textColor: 'text-red-500', bgLight: 'bg-red-500/10' },
  closed: { label: 'Closed', color: 'bg-green-500', textColor: 'text-green-500', bgLight: 'bg-green-500/10' },
};

// Severity configuration
const SEVERITY_CONFIG = {
  critical: { label: 'Critical', color: 'bg-red-500', textColor: 'text-red-500' },
  high: { label: 'High', color: 'bg-orange-500', textColor: 'text-orange-500' },
  medium: { label: 'Medium', color: 'bg-yellow-500', textColor: 'text-yellow-500' },
  low: { label: 'Low', color: 'bg-green-500', textColor: 'text-green-500' },
};

// Stats Card Component
const StatCard = ({ title, value, icon: Icon, color, subtext }) => (
  <Card className="bg-card border-border">
    <CardContent className="p-3 sm:p-4">
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <p className="text-xs sm:text-sm text-muted-foreground truncate">{title}</p>
          <p className={`text-xl sm:text-2xl font-bold ${color || ''}`}>{value}</p>
          {subtext && <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">{subtext}</p>}
        </div>
        <div className={`p-2 sm:p-3 rounded-full flex-shrink-0 ${color ? color.replace('text-', 'bg-') + '/10' : 'bg-secondary'}`}>
          <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${color || 'text-muted-foreground'}`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

// Incident Card Component for Kanban (Draggable)
const DraggableIncidentCard = ({ incident, onView, onAssign, onEscalate, onAddNote, onStatusChange, admins, isSelected, onSelect, isDragging }) => {
  const CategoryIcon = CATEGORY_ICONS[incident.primary_category] || Shield;
  const severityConfig = SEVERITY_CONFIG[incident.severity] || SEVERITY_CONFIG.medium;
  const [showActions, setShowActions] = useState(false);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ id: incident.id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isSortableDragging ? 0.5 : 1,
  };
  
  return (
    <div 
      ref={setNodeRef}
      style={style}
      className={`p-3 rounded-lg border bg-card hover:border-primary/50 transition-all ${
        isSelected ? 'border-primary ring-2 ring-primary/20' : 'border-border'
      } ${isSortableDragging ? 'shadow-lg ring-2 ring-primary' : ''}`}
    >
      <div className="flex items-start gap-2">
        {/* Drag Handle */}
        <div 
          {...attributes} 
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-secondary/50 rounded mt-0.5"
        >
          <GripVertical className="w-4 h-4 text-muted-foreground" />
        </div>
        
        <Checkbox 
          checked={isSelected}
          onCheckedChange={(checked) => {
            onSelect(incident.id, checked);
          }}
          onClick={(e) => e.stopPropagation()}
          className="mt-1"
        />
        <div 
          className="flex-1 min-w-0 cursor-pointer"
          onClick={() => onView(incident.id)}
        >
          <div className="flex items-center gap-2 mb-1">
            <CategoryIcon className={`w-4 h-4 flex-shrink-0 ${
              incident.primary_category === 'abuse_misconduct' ? 'text-red-500' : 'text-muted-foreground'
            }`} />
            <span className="font-medium text-sm truncate">{incident.title}</span>
          </div>
          
          <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
            {incident.description}
          </p>
          
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={`${severityConfig.color}/20 ${severityConfig.textColor} text-xs`}>
              {severityConfig.label}
            </Badge>
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {new Date(incident.incident_date).toLocaleDateString()}
            </span>
            {incident.assigned_admin_name && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <UserPlus className="w-3 h-3" />
                {incident.assigned_admin_name}
              </span>
            )}
          </div>
          
          {incident.ai_analysis?.structured && (
            <div className="mt-2 p-2 rounded bg-primary/5 border border-primary/20">
              <p className="text-xs text-primary flex items-center gap-1">
                <Zap className="w-3 h-3" />
                Risk: {incident.ai_analysis.structured.risk_assessment?.overall_risk_score}/10 
                • {incident.ai_analysis.structured.risk_assessment?.liability_level} Liability
              </p>
            </div>
          )}
        </div>
        
        <div className="relative">
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-8 w-8"
            onClick={(e) => {
              e.stopPropagation();
              setShowActions(!showActions);
            }}
          >
            <MoreVertical className="w-4 h-4" />
          </Button>
          
          {showActions && (
            <div className="absolute right-0 top-8 z-50 w-48 bg-popover border border-border rounded-lg shadow-lg py-1">
              <button 
                className="w-full px-3 py-2 text-left text-sm hover:bg-secondary flex items-center gap-2"
                onClick={(e) => { e.stopPropagation(); onView(incident.id); }}
              >
                <Eye className="w-4 h-4" /> View Details
              </button>
              <button 
                className="w-full px-3 py-2 text-left text-sm hover:bg-secondary flex items-center gap-2"
                onClick={(e) => { e.stopPropagation(); onAssign(incident); }}
              >
                <UserPlus className="w-4 h-4" /> Assign Admin
              </button>
              <button 
                className="w-full px-3 py-2 text-left text-sm hover:bg-secondary flex items-center gap-2"
                onClick={(e) => { e.stopPropagation(); onAddNote(incident); }}
              >
                <MessageSquare className="w-4 h-4" /> Add Note
              </button>
              {incident.status !== 'escalated' && (
                <button 
                  className="w-full px-3 py-2 text-left text-sm hover:bg-secondary text-red-500 flex items-center gap-2"
                  onClick={(e) => { e.stopPropagation(); onEscalate(incident); }}
                >
                  <AlertCircle className="w-4 h-4" /> Escalate
                </button>
              )}
              <Separator className="my-1" />
              {Object.entries(STATUS_CONFIG).map(([status, config]) => (
                status !== incident.status && (
                  <button 
                    key={status}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-secondary flex items-center gap-2"
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      onStatusChange(incident.id, status);
                      setShowActions(false);
                    }}
                  >
                    <div className={`w-2 h-2 rounded-full ${config.color}`} />
                    Move to {config.label}
                  </button>
                )
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Drag Overlay Card (shown while dragging)
const DragOverlayCard = ({ incident }) => {
  if (!incident) return null;
  
  const CategoryIcon = CATEGORY_ICONS[incident.primary_category] || Shield;
  const severityConfig = SEVERITY_CONFIG[incident.severity] || SEVERITY_CONFIG.medium;
  
  return (
    <div className="p-3 rounded-lg border-2 border-primary bg-card shadow-2xl w-[280px] opacity-95">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <CategoryIcon className={`w-4 h-4 flex-shrink-0 ${
              incident.primary_category === 'abuse_misconduct' ? 'text-red-500' : 'text-muted-foreground'
            }`} />
            <span className="font-medium text-sm truncate">{incident.title}</span>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <Badge className={`${severityConfig.color}/20 ${severityConfig.textColor} text-xs`}>
              {severityConfig.label}
            </Badge>
          </div>
        </div>
      </div>
    </div>
  );
};

// Kanban Column Component (Droppable)
const KanbanColumn = ({ status, incidents, onView, onAssign, onEscalate, onAddNote, onStatusChange, admins, selectedIncidents, onSelectIncident, isOver }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.new;
  
  return (
    <div 
      className={`flex-shrink-0 w-[260px] sm:w-[280px] md:flex-1 md:min-w-[250px] md:max-w-[320px] transition-all ${isOver ? 'scale-[1.02]' : ''}`}
      data-status={status}
    >
      <div className={`p-2 sm:p-3 rounded-t-lg ${config.bgLight} border-b-2 ${config.color.replace('bg-', 'border-')} ${isOver ? 'ring-2 ring-primary ring-offset-2' : ''}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 sm:gap-2">
            <div className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full ${config.color}`} />
            <span className="font-medium text-xs sm:text-sm">{config.label}</span>
          </div>
          <Badge variant="outline" className="text-[10px] sm:text-xs">{incidents.length}</Badge>
        </div>
      </div>
      <div className={`bg-secondary/10 rounded-b-lg border border-t-0 ${isOver ? 'border-primary bg-primary/5' : 'border-border'}`}>
        <ScrollArea className="h-[calc(100vh-450px)] sm:h-[calc(100vh-400px)] min-h-[300px] sm:min-h-[400px]">
          <SortableContext items={incidents.map(i => i.id)} strategy={verticalListSortingStrategy}>
            <div className="p-1.5 sm:p-2 space-y-1.5 sm:space-y-2">
              {incidents.map(incident => (
                <DraggableIncidentCard
                  key={incident.id}
                  incident={incident}
                  onView={onView}
                  onAssign={onAssign}
                  onEscalate={onEscalate}
                  onAddNote={onAddNote}
                  onStatusChange={onStatusChange}
                  admins={admins}
                  isSelected={selectedIncidents.includes(incident.id)}
                  onSelect={onSelectIncident}
                />
              ))}
              {incidents.length === 0 && (
                <div className={`text-center py-6 sm:py-8 text-muted-foreground text-xs sm:text-sm border-2 border-dashed rounded-lg ${isOver ? 'border-primary bg-primary/10' : 'border-border'}`}>
                  {isOver ? 'Drop here' : 'No incidents'}
                </div>
              )}
            </div>
          </SortableContext>
        </ScrollArea>
      </div>
    </div>
  );
};

export default function IncidentWorkflowDashboard() {
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();
  const { selectedOrg, isOrganizationMode } = useAdminMode();
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [board, setBoard] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [viewMode, setViewMode] = useState('kanban'); // kanban or list
  const [selectedIncidents, setSelectedIncidents] = useState([]);
  
  // Drag and drop state
  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);
  
  // Dialog states
  const [assignDialog, setAssignDialog] = useState({ open: false, incident: null });
  const [escalateDialog, setEscalateDialog] = useState({ open: false, incident: null });
  const [noteDialog, setNoteDialog] = useState({ open: false, incident: null });
  const [bulkDialog, setBulkDialog] = useState({ open: false });
  
  // Form states
  const [selectedAdminId, setSelectedAdminId] = useState('');
  const [escalationReason, setEscalationReason] = useState('');
  const [noteText, setNoteText] = useState('');
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkAdminId, setBulkAdminId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Configure DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required before drag starts
      },
    }),
    useSensor(KeyboardSensor)
  );
  
  const currentOrgId = selectedOrg?.id || user?.organization_id;
  
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [statsRes, boardRes, adminsRes] = await Promise.all([
        getIncidentWorkflowStats(currentOrgId),
        getIncidentWorkflowBoard(currentOrgId),
        getAdminList(currentOrgId)
      ]);
      setStats(statsRes.data);
      setBoard(boardRes.data);
      setAdmins(adminsRes.data.admins || []);
    } catch (error) {
      console.error('Failed to fetch workflow data:', error);
      toast.error('Failed to load incident workflow');
    } finally {
      setLoading(false);
    }
  }, [currentOrgId]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  const handleViewIncident = (incidentId) => {
    navigate(`/incidents/${incidentId}`);
  };
  
  const handleAssign = async () => {
    if (!selectedAdminId || !assignDialog.incident) return;
    
    setIsSubmitting(true);
    try {
      await assignIncident(assignDialog.incident.id, selectedAdminId);
      toast.success('Incident assigned successfully');
      setAssignDialog({ open: false, incident: null });
      setSelectedAdminId('');
      fetchData();
    } catch (error) {
      toast.error('Failed to assign incident');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleEscalate = async () => {
    if (!escalationReason.trim() || !escalateDialog.incident) return;
    
    setIsSubmitting(true);
    try {
      await escalateIncident(escalateDialog.incident.id, escalationReason);
      toast.success('Incident escalated');
      setEscalateDialog({ open: false, incident: null });
      setEscalationReason('');
      fetchData();
    } catch (error) {
      toast.error('Failed to escalate incident');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleAddNote = async () => {
    if (!noteText.trim() || !noteDialog.incident) return;
    
    setIsSubmitting(true);
    try {
      await addIncidentNote(noteDialog.incident.id, { note: noteText, is_private: true });
      toast.success('Note added');
      setNoteDialog({ open: false, incident: null });
      setNoteText('');
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleStatusChange = async (incidentId, newStatus) => {
    try {
      await updateIncident(incidentId, { status: newStatus });
      toast.success(`Status updated to ${STATUS_CONFIG[newStatus]?.label}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };
  
  const handleBulkUpdate = async () => {
    if (selectedIncidents.length === 0) return;
    if (!bulkStatus && !bulkAdminId) {
      toast.error('Select a status or admin to update');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await bulkUpdateIncidents({
        incident_ids: selectedIncidents,
        status: bulkStatus || undefined,
        assigned_admin_id: bulkAdminId || undefined
      });
      toast.success(`Updated ${selectedIncidents.length} incidents`);
      setBulkDialog({ open: false });
      setSelectedIncidents([]);
      setBulkStatus('');
      setBulkAdminId('');
      fetchData();
    } catch (error) {
      toast.error('Failed to update incidents');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleSelectIncident = (incidentId, selected) => {
    setSelectedIncidents(prev => 
      selected 
        ? [...prev, incidentId]
        : prev.filter(id => id !== incidentId)
    );
  };
  
  const handleSelectAll = (status) => {
    const statusIncidents = board?.[status]?.map(i => i.id) || [];
    setSelectedIncidents(prev => {
      const allSelected = statusIncidents.every(id => prev.includes(id));
      if (allSelected) {
        return prev.filter(id => !statusIncidents.includes(id));
      } else {
        return [...new Set([...prev, ...statusIncidents])];
      }
    });
  };
  
  // Drag and drop handlers
  const findIncidentById = (id) => {
    if (!board) return null;
    for (const status of Object.keys(board)) {
      const incident = board[status]?.find(i => i.id === id);
      if (incident) return { incident, status };
    }
    return null;
  };
  
  const findStatusFromPosition = (overId) => {
    if (!overId) return null;
    // Check if overId is a status column
    if (STATUS_CONFIG[overId]) return overId;
    // Check if overId is an incident - find its status
    const result = findIncidentById(overId);
    return result?.status || null;
  };
  
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };
  
  const handleDragOver = (event) => {
    const { over } = event;
    if (over) {
      // Find which column we're over
      const element = document.querySelector(`[data-status]`);
      const columns = document.querySelectorAll('[data-status]');
      let targetStatus = null;
      
      columns.forEach((col) => {
        const rect = col.getBoundingClientRect();
        if (event.activatorEvent) {
          // Use the over.id to determine target status
          targetStatus = findStatusFromPosition(over.id);
        }
      });
      
      setOverId(targetStatus || findStatusFromPosition(over.id));
    } else {
      setOverId(null);
    }
  };
  
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    setOverId(null);
    
    if (!over || !active) return;
    
    const activeIncidentData = findIncidentById(active.id);
    if (!activeIncidentData) return;
    
    // Determine target status
    let targetStatus = findStatusFromPosition(over.id);
    
    // If dropped directly on a column header area
    if (!targetStatus && over.id) {
      targetStatus = over.id;
    }
    
    if (!targetStatus || targetStatus === activeIncidentData.status) {
      return; // No change needed
    }
    
    // Optimistically update the board
    const newBoard = { ...board };
    const incident = activeIncidentData.incident;
    
    // Remove from old status
    newBoard[activeIncidentData.status] = newBoard[activeIncidentData.status].filter(
      i => i.id !== active.id
    );
    
    // Add to new status
    newBoard[targetStatus] = [...(newBoard[targetStatus] || []), { ...incident, status: targetStatus }];
    
    setBoard(newBoard);
    
    // Make API call
    try {
      await updateIncident(active.id, { status: targetStatus });
      toast.success(`Moved to ${STATUS_CONFIG[targetStatus]?.label}`);
    } catch (error) {
      // Revert on error
      toast.error('Failed to update status');
      fetchData();
    }
  };
  
  const getActiveIncident = () => {
    if (!activeId) return null;
    const result = findIncidentById(activeId);
    return result?.incident || null;
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <div className="space-y-4 sm:space-y-6" data-testid="incident-workflow-dashboard">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold font-['Barlow_Condensed'] uppercase tracking-tight">
            Incident Workflow
          </h2>
          <p className="text-muted-foreground text-xs sm:text-sm">
            Manage and track incidents across your organization
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {selectedIncidents.length > 0 && (
            <Button onClick={() => setBulkDialog({ open: true })} variant="outline" size="sm" className="gap-1.5 sm:gap-2 text-xs sm:text-sm h-8 sm:h-9">
              <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Bulk Update</span>
              <span className="sm:hidden">Bulk</span>
              ({selectedIncidents.length})
            </Button>
          )}
          <Button onClick={fetchData} variant="outline" size="icon" className="h-8 w-8 sm:h-9 sm:w-9">
            <RefreshCw className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          </Button>
          <div className="flex items-center border rounded-lg p-0.5 sm:p-1 bg-secondary/30">
            <Button 
              variant={viewMode === 'kanban' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('kanban')}
              className="gap-1 sm:gap-2 h-7 sm:h-8 px-2 sm:px-3 text-xs sm:text-sm"
            >
              <LayoutGrid className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Board</span>
            </Button>
            <Button 
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className="gap-1 sm:gap-2 h-7 sm:h-8 px-2 sm:px-3 text-xs sm:text-sm"
            >
              <List className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">List</span>
            </Button>
          </div>
        </div>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-4">
        <StatCard 
          title="Total Incidents" 
          value={stats?.total || 0} 
          icon={FileText}
        />
        <StatCard 
          title="New (24h)" 
          value={stats?.recent_24h || 0} 
          icon={Clock}
          color="text-blue-500"
        />
        <StatCard 
          title="Unassigned" 
          value={stats?.unassigned || 0} 
          icon={UserPlus}
          color="text-yellow-500"
        />
        <StatCard 
          title="Escalated" 
          value={stats?.by_status?.escalated || 0} 
          icon={AlertCircle}
          color="text-red-500"
        />
        <StatCard 
          title="Overdue" 
          value={stats?.overdue || 0} 
          icon={AlertTriangle}
          color="text-orange-500"
          subtext="Critical/High > 48h"
        />
      </div>
      
      {/* Kanban Board with Drag and Drop */}
      {viewMode === 'kanban' && board && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <div className="flex gap-2 sm:gap-4 overflow-x-auto pb-4 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide snap-x snap-mandatory md:snap-none">
            {Object.entries(STATUS_CONFIG).map(([status]) => (
              <KanbanColumn
                key={status}
                status={status}
                incidents={board[status] || []}
                onView={handleViewIncident}
                onAssign={(incident) => setAssignDialog({ open: true, incident })}
                onEscalate={(incident) => setEscalateDialog({ open: true, incident })}
                onAddNote={(incident) => setNoteDialog({ open: true, incident })}
                onStatusChange={handleStatusChange}
                admins={admins}
                selectedIncidents={selectedIncidents}
                onSelectIncident={handleSelectIncident}
                isOver={overId === status}
              />
            ))}
          </div>
          <DragOverlay>
            <DragOverlayCard incident={getActiveIncident()} />
          </DragOverlay>
        </DndContext>
      )}
      
      {/* List View */}
      {viewMode === 'list' && board && (
        <Card className="bg-card border-border overflow-hidden">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="border-b border-border">
                  <th className="p-2 sm:p-3 text-left text-xs sm:text-sm font-medium">
                    <Checkbox 
                      checked={selectedIncidents.length > 0}
                      onCheckedChange={() => {
                        const allIds = Object.values(board).flat().map(i => i.id);
                        setSelectedIncidents(prev => prev.length === allIds.length ? [] : allIds);
                      }}
                    />
                  </th>
                  <th className="p-3 text-left text-sm font-medium">Incident</th>
                  <th className="p-3 text-left text-sm font-medium">Category</th>
                  <th className="p-3 text-left text-sm font-medium">Severity</th>
                  <th className="p-3 text-left text-sm font-medium">Status</th>
                  <th className="p-3 text-left text-sm font-medium">Assigned</th>
                  <th className="p-3 text-left text-sm font-medium">Date</th>
                  <th className="p-3 text-left text-sm font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {Object.values(board).flat().map(incident => {
                  const CategoryIcon = CATEGORY_ICONS[incident.primary_category] || Shield;
                  const severityConfig = SEVERITY_CONFIG[incident.severity] || SEVERITY_CONFIG.medium;
                  const statusConfig = STATUS_CONFIG[incident.status] || STATUS_CONFIG.new;
                  
                  return (
                    <tr 
                      key={incident.id} 
                      className="border-b border-border hover:bg-secondary/30 cursor-pointer"
                      onClick={() => handleViewIncident(incident.id)}
                    >
                      <td className="p-3" onClick={(e) => e.stopPropagation()}>
                        <Checkbox 
                          checked={selectedIncidents.includes(incident.id)}
                          onCheckedChange={(checked) => handleSelectIncident(incident.id, checked)}
                        />
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <CategoryIcon className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium text-sm">{incident.title}</span>
                        </div>
                      </td>
                      <td className="p-3">
                        <span className="text-sm">{incident.secondary_type || incident.category}</span>
                      </td>
                      <td className="p-3">
                        <Badge className={`${severityConfig.color}/20 ${severityConfig.textColor} text-xs`}>
                          {severityConfig.label}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <Badge className={`${statusConfig.bgLight} ${statusConfig.textColor} text-xs`}>
                          {statusConfig.label}
                        </Badge>
                      </td>
                      <td className="p-3 text-sm text-muted-foreground">
                        {incident.assigned_admin_name || '-'}
                      </td>
                      <td className="p-3 text-sm text-muted-foreground">
                        {new Date(incident.incident_date).toLocaleDateString()}
                      </td>
                      <td className="p-3">
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
      
      {/* Assign Dialog */}
      <Dialog open={assignDialog.open} onOpenChange={(open) => setAssignDialog({ open, incident: null })}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Assign Incident</DialogTitle>
            <DialogDescription>
              Assign "{assignDialog.incident?.title}" to an admin
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Select value={selectedAdminId} onValueChange={setSelectedAdminId}>
              <SelectTrigger>
                <SelectValue placeholder="Select an admin..." />
              </SelectTrigger>
              <SelectContent>
                {admins.map(admin => (
                  <SelectItem key={admin.id} value={admin.id}>
                    {admin.name || admin.email} ({admin.role})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialog({ open: false, incident: null })}>
              Cancel
            </Button>
            <Button onClick={handleAssign} disabled={!selectedAdminId || isSubmitting}>
              {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Escalate Dialog */}
      <Dialog open={escalateDialog.open} onOpenChange={(open) => setEscalateDialog({ open, incident: null })}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-red-500 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Escalate Incident
            </DialogTitle>
            <DialogDescription>
              Escalate "{escalateDialog.incident?.title}" for immediate attention
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Textarea
              value={escalationReason}
              onChange={(e) => setEscalationReason(e.target.value)}
              placeholder="Reason for escalation..."
              rows={4}
              className="bg-secondary/50"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEscalateDialog({ open: false, incident: null })}>
              Cancel
            </Button>
            <Button 
              onClick={handleEscalate} 
              disabled={!escalationReason.trim() || isSubmitting}
              className="bg-red-500 hover:bg-red-600"
            >
              {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Escalate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Add Note Dialog */}
      <Dialog open={noteDialog.open} onOpenChange={(open) => setNoteDialog({ open, incident: null })}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Add Internal Note
            </DialogTitle>
            <DialogDescription>
              Add a private note to "{noteDialog.incident?.title}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Enter your note..."
              rows={4}
              className="bg-secondary/50"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteDialog({ open: false, incident: null })}>
              Cancel
            </Button>
            <Button onClick={handleAddNote} disabled={!noteText.trim() || isSubmitting}>
              {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Send className="w-4 h-4 mr-2" />
              Add Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Bulk Update Dialog */}
      <Dialog open={bulkDialog.open} onOpenChange={(open) => setBulkDialog({ open })}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Bulk Update Incidents</DialogTitle>
            <DialogDescription>
              Update {selectedIncidents.length} selected incidents
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Change Status</label>
              <Select value={bulkStatus} onValueChange={setBulkStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status..." />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_CONFIG).map(([value, config]) => (
                    <SelectItem key={value} value={value}>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${config.color}`} />
                        {config.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Assign To</label>
              <Select value={bulkAdminId} onValueChange={setBulkAdminId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select admin..." />
                </SelectTrigger>
                <SelectContent>
                  {admins.map(admin => (
                    <SelectItem key={admin.id} value={admin.id}>
                      {admin.name || admin.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDialog({ open: false })}>
              Cancel
            </Button>
            <Button onClick={handleBulkUpdate} disabled={isSubmitting || (!bulkStatus && !bulkAdminId)}>
              {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Update {selectedIncidents.length} Incidents
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
